/**
 * What every site route renders: the right page, lazily, with the app's
 * router handed to the site's links. The pages themselves never import the
 * router, so they render the same in Node (the prerender) and in a test.
 *
 * Like routes.tsx this file is loaded with the app shell, so it holds nothing
 * but the lazy imports and the URL of the news notes file.
 */
import { lazy, Suspense, useCallback, type ReactNode } from 'react';
import { Navigate, useParams, useRouter } from '@tanstack/react-router';
import newsUrl from 'virtual:site-news';
import { SiteNavContext } from './parts/siteNav';
import type { Door, NewsNote } from './tour/types';

// sitenotes: the news notes are one JSON file, not site JS (scripts/site-news.ts).
// A page that shows a note fetches it beside its own chunk and hands it over
// before it draws, so a note never pops in and no round trip is added. Offline
// or refused, the page draws without notes and the next page asks again.
let news: Promise<readonly NewsNote[]> | null = null;
const loadNews = () => (news ??= fetch(newsUrl)
    .then((r) => (r.ok ? (r.json() as Promise<NewsNote[]>) : Promise.reject(new Error(String(r.status)))))
    .catch(() => {
        news = null;
        return [];
    }));
function withNews<T>(page: () => Promise<T>): () => Promise<T> {
    return () => Promise.all([page(), import('./tour/newsStore'), loadNews()]).then(([m, store, list]) => {
        store.setNews(list);
        return m;
    });
}

const SiteRoot = lazy(withNews(() => import('./pages/SiteRoot')));
const CountryHome = lazy(withNews(() => import('./pages/CountryHome')));
const DoorPage = lazy(() => import('./pages/DoorPage'));
const Everything = lazy(() => import('./pages/Everything'));
const NewPage = lazy(withNews(() => import('./pages/NewPage')));
const EntryPage = lazy(withNews(() => import('./pages/EntryPage')));
// The film lab exists in development only; the condition is replaced at build
// time, so production carries neither the page nor its drawn fixture frames.
const FilmLab = import.meta.env.DEV ? lazy(() => import('./pages/FilmLab')) : null;

function SiteShell({ children }: { children: ReactNode }) {
    const router = useRouter();
    const go = useCallback((href: string) => {
        router.history.push(href);
        window.scrollTo(0, 0);
    }, [router]);
    return (
        <SiteNavContext.Provider value={go}>
            <Suspense fallback={<div className="min-h-screen bg-stone-50 dark:bg-stone-950" />}>{children}</Suspense>
        </SiteNavContext.Provider>
    );
}

export type SitePageKind = 'root' | 'home' | 'door' | 'everything' | 'new' | 'film-lab';

export function SitePage({ page, cc = 'IN', door = 'business', extras }: { page: SitePageKind; cc?: string; door?: Door; extras?: ReactNode }) {
    return (
        <SiteShell>
            {page === 'root' && <SiteRoot extras={extras} />}
            {page === 'home' && <CountryHome cc={cc} extras={extras} />}
            {page === 'door' && <DoorPage cc={cc} door={door} />}
            {page === 'everything' && <Everything cc={cc} />}
            {page === 'new' && <NewPage cc={cc} />}
            {page === 'film-lab' && FilmLab && <FilmLab />}
        </SiteShell>
    );
}

export function TourEntryRoute() {
    const { id } = useParams({ strict: false }) as { id?: string };
    return <SiteShell><EntryPage id={id ?? ''} /></SiteShell>;
}

/** The site's home for a guest at `/`, rendered by the app's landing route (sitetour-12). */
export function SiteRootPage({ extras }: { extras?: ReactNode }) {
    return <SitePage page="root" extras={extras} />;
}

/** An old `/next/...` link, opened inside the app, lands on the page it now has (sitetour-12). */
export function FromPreview() {
    const { _splat } = useParams({ strict: false }) as { _splat?: string };
    return <Navigate to={`/${_splat ?? ''}`} replace />;
}
